import { useEffect, useState } from 'react';
import { api } from '../api.ts';
import { useApp } from '../store.tsx';
import { Card, Field, Spinner } from '../components.tsx';

/**
 * Where the links in our emails land.
 *
 * There is no router in this app — the server returns index.html for any path —
 * so the two emailed destinations are recognised here by pathname and handled
 * before anything else renders. Both must work with no session at all: someone
 * who has forgotten their password cannot be asked to sign in first, and a
 * confirmation link is usually opened in whichever browser the mail app
 * chooses rather than in the installed app.
 */

export type LandingKind = 'verify-email' | 'reset-password';

export function detectLanding(): { kind: LandingKind; token: string } | null {
  const path = window.location.pathname;
  if (path !== '/verify-email' && path !== '/reset-password') return null;
  const token = new URLSearchParams(window.location.search).get('token');
  if (!token) return null;
  return { kind: path === '/verify-email' ? 'verify-email' : 'reset-password', token };
}

/** Drops the token from the address bar once it has been used or has failed. */
function clearUrl(): void {
  window.history.replaceState({}, '', '/');
}

export function EmailLanding({ kind, token, onDone }: {
  kind: LandingKind;
  token: string;
  onDone: () => void;
}): JSX.Element {
  return kind === 'verify-email'
    ? <VerifyEmail token={token} onDone={onDone} />
    : <ResetPassword token={token} onDone={onDone} />;
}

function Shell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="screen" style={{
      minHeight: '100dvh', display: 'flex', flexDirection: 'column',
      justifyContent: 'center', maxWidth: 440, marginInline: 'auto',
    }}>
      <div style={{ fontSize: 30, marginBottom: 20 }} aria-hidden>◈</div>
      {children}
    </div>
  );
}

function VerifyEmail({ token, onDone }: { token: string; onDone: () => void }): JSX.Element {
  const { t, locale } = useApp();
  const ar = locale === 'ar';
  const [state, setState] = useState<'working' | 'done' | 'failed'>('working');
  const [message, setMessage] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.post('/auth/verify-email', { token })
      .then(() => { if (!cancelled) { setState('done'); clearUrl(); } })
      .catch((err: Error) => {
        if (cancelled) return;
        setMessage(err.message);
        setState('failed');
        clearUrl();
      });
    return () => { cancelled = true; };
  }, [token]);

  if (state === 'working') {
    return <Shell><Spinner label={ar ? 'جارٍ التأكيد…' : 'Confirming…'} /></Shell>;
  }

  return (
    <Shell>
      <h1 style={{ fontSize: 22, fontWeight: 680, marginBottom: 8 }}>
        {state === 'done'
          ? (ar ? 'تم تأكيد بريدك' : 'Email confirmed')
          : (ar ? 'تعذّر التأكيد' : 'Could not confirm')}
      </h1>
      <p className="small muted" style={{ marginBottom: 20, lineHeight: 1.6 }}>
        {state === 'done'
          ? (ar
              ? 'يمكنك الآن استعادة حسابك إن نسيت كلمة المرور.'
              : 'You can now recover your account if you ever forget your password.')
          : message}
      </p>
      <button className="btn btn--primary btn--block" onClick={onDone}>
        {ar ? 'فتح فينتراك' : 'Open Fintrack'}
      </button>
      <p className="tiny muted center" style={{ marginTop: 14 }}>{t('appName')}</p>
    </Shell>
  );
}

function ResetPassword({ token, onDone }: { token: string; onDone: () => void }): JSX.Element {
  const { locale, toast } = useApp();
  const ar = locale === 'ar';
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/reset-password', { token, password });
      clearUrl();
      setDone(true);
      toast(ar ? 'تم تغيير كلمة المرور' : 'Password changed');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Shell>
        <h1 style={{ fontSize: 22, fontWeight: 680, marginBottom: 8 }}>
          {ar ? 'تم تغيير كلمة المرور' : 'Password changed'}
        </h1>
        <p className="small muted" style={{ marginBottom: 20, lineHeight: 1.6 }}>
          {ar
            ? 'سُجّل خروج بقية الأجهزة. سجّل الدخول بكلمة المرور الجديدة.'
            : 'Every other device has been signed out. Sign in with your new password.'}
        </p>
        <button className="btn btn--primary btn--block" onClick={onDone}>
          {ar ? 'تسجيل الدخول' : 'Sign in'}
        </button>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 style={{ fontSize: 22, fontWeight: 680, marginBottom: 8 }}>
        {ar ? 'كلمة مرور جديدة' : 'Choose a new password'}
      </h1>
      <form onSubmit={submit} className="stack">
        <Field
          label={ar ? 'كلمة المرور الجديدة' : 'New password'}
          hint={ar ? '١٠ أحرف على الأقل' : 'At least 10 characters'}
          error={error}
        >
          <input className="input" type="password" required minLength={10} autoFocus
                 value={password} onChange={(e) => setPassword(e.target.value)}
                 autoComplete="new-password" />
        </Field>
        <button className="btn btn--primary btn--block" type="submit" disabled={busy || password.length < 10}>
          {busy ? '…' : (ar ? 'حفظ' : 'Save password')}
        </button>
      </form>
      <Card style={{ marginTop: 18, background: 'var(--bg-sunken)' }}>
        <p className="tiny muted" style={{ lineHeight: 1.6 }}>
          {ar
            ? 'ينتهي هذا الرابط بعد ساعة ويُستخدم مرة واحدة.'
            : 'This link expires an hour after it was sent and can be used once.'}
        </p>
      </Card>
    </Shell>
  );
}
